/*
 * Shared controller for native page forms (warranty / dealer / contact).
 *
 * Classic, deferred, vanilla JS (independent of the custom.js ES module, which
 * does not reliably bind on the minified live storefront).
 *
 * Two modes per <form class="native-form__form">:
 *   - No `data-endpoint`: native full-page submit (Shopify /contact). We only
 *     block honeypot bots and show a "Sending…" loading state.
 *   - With `data-endpoint`: AJAX upload submit to our own endpoint (Cloudflare
 *     Worker) so a file can be attached; success/error shown in-page, no reload.
 *     Used by sections/native-form-dealer.liquid.
 */
(function () {
  "use strict";

  // The Worker answers with a typed code (see serverless/forms-worker/src/index.js).
  // Without this mapping a rejected 12 MB scan just shows "something went wrong",
  // which tells the applicant nothing actionable.
  var ERRORS = {
    file_too_large: "That file is larger than 10 MB. Please attach a smaller PDF or image.",
    file_type_not_allowed: "That file type isn't supported. Please attach a PDF, JPG or PNG.",
    email_required: "Please enter your email address.",
    invalid_form: "We couldn't read that submission. Please check the fields and try again.",
    send_failed: "We couldn't deliver your submission right now. Please email info@bajabuiltwheels.com directly.",
    captcha_failed: "We couldn't verify you're human. Please wait a moment and try again.",
    too_many_files: "Please attach up to 5 photos.",
    files_too_large: "Those photos are too large together. Please remove one or two and try again.",
    bad_links: "One of your links doesn't look right. Each link should start with http:// or https://, one per line.",
  };
  var ERROR_FALLBACK =
    "Something went wrong sending your submission. Please try again, or email info@bajabuiltwheels.com.";

  // Turnstile is loaded on first interaction rather than on page load. The
  // enquiry form sits behind a modal on every product page and is rarely
  // opened, so fetching Cloudflare's script for every product view would tax
  // our highest-traffic pages to protect a form most visitors never see.
  var TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  var turnstileRequested = false;

  function renderWidgets() {
    if (!window.turnstile) return;
    var slots = document.querySelectorAll("[data-turnstile]");
    for (var i = 0; i < slots.length; i++) {
      var slot = slots[i];
      if (slot.getAttribute("data-widget-id")) continue;
      var key = slot.getAttribute("data-sitekey");
      if (!key) continue;
      slot.setAttribute("data-widget-id", window.turnstile.render(slot, { sitekey: key }));
    }
  }

  function loadTurnstile() {
    if (!document.querySelector("[data-turnstile]")) return;
    renderWidgets();
    if (turnstileRequested) return;
    turnstileRequested = true;
    var s = document.createElement("script");
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.onload = renderWidgets;
    document.head.appendChild(s);
  }

  // enquiry-modal.js calls this when the modal opens; the focusin listener
  // below covers forms that are already visible on the page (dealer).
  window.BajaTurnstile = { load: loadTurnstile };

  document.addEventListener("focusin", function (e) {
    if (e.target && e.target.closest && e.target.closest(".native-form__form")) {
      loadTurnstile();
    }
  });

  function resetTurnstile(form) {
    var slot = form.querySelector("[data-turnstile]");
    if (!slot || !window.turnstile) return;
    var id = slot.getAttribute("data-widget-id");
    // Tokens are single-use. Without this, anyone who hits an error stays stuck
    // on a spent token and every retry fails.
    if (id) window.turnstile.reset(id);
  }

  // The theme forces its condensed display face ("Rift") onto form labels from
  // theme.css with !important, and it outranks even a four-class selector with
  // !important in our own stylesheet. That face is designed for headings and is
  // genuinely hard to read at label size, which is what made these forms hard
  // to use. An inline important declaration is the one thing that reliably wins,
  // so labels get their typography set here rather than in native-form.css.
  function forceLabelTypography() {
    var labels = document.querySelectorAll(".native-form__group > label");
    for (var i = 0; i < labels.length; i++) {
      var s = labels[i].style;
      s.setProperty("font-family", "Roboto, Arial, sans-serif", "important");
      s.setProperty("font-style", "normal", "important");
      s.setProperty("font-size", "15px", "important");
      s.setProperty("font-weight", "600", "important");
      s.setProperty("letter-spacing", "0.01em", "important");
      s.setProperty("text-transform", "none", "important");
    }
    // Typed text in message boxes: the theme forces the display face onto textareas too
    // (single-line inputs already render in Roboto), so what a customer types in "How can
    // we help?" came out in the condensed face. Keep it in step with the rest of the form.
    var areas = document.querySelectorAll(".native-form__group textarea");
    for (var k = 0; k < areas.length; k++) {
      var a = areas[k].style;
      a.setProperty("font-family", "Roboto, Arial, sans-serif", "important");
      a.setProperty("font-style", "normal", "important");
      a.setProperty("text-transform", "none", "important");
    }
    // Same treatment for the smaller supporting text (upload hint, intro line).
    var notes = document.querySelectorAll(".native-form__hint, .native-form__intro");
    for (var j = 0; j < notes.length; j++) {
      var n = notes[j].style;
      n.setProperty("font-family", "Roboto, Arial, sans-serif", "important");
      n.setProperty("font-style", "normal", "important");
      n.setProperty("text-transform", "none", "important");
      n.setProperty("font-weight", "400", "important");
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", forceLabelTypography);
  } else {
    forceLabelTypography();
  }

  // Success is shown in a fixed overlay so the page never scrolls or reflows
  // underneath the reader. The inline success block still exists behind it as
  // the state they return to after closing, and as a no-JS-modal fallback.
  function showSuccessModal(sourceEl, form) {
    var overlay = document.createElement("div");
    overlay.className = "native-form__modal";
    var card = document.createElement("div");
    card.className = "native-form__modal-card";
    card.setAttribute("role", "dialog");
    card.setAttribute("aria-modal", "true");

    var closeX = document.createElement("button");
    closeX.type = "button";
    closeX.className = "native-form__modal-close";
    closeX.setAttribute("aria-label", "Close");
    closeX.textContent = "×";

    // Clone the section's own thank-you markup rather than assigning innerHTML.
    var body = sourceEl.cloneNode(true);
    body.removeAttribute("style");
    body.removeAttribute("data-native-state");
    body.className = "native-form__modal-body";

    var closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "native-form__modal-btn";
    closeBtn.textContent = "Close";

    card.appendChild(closeX);
    card.appendChild(body);
    card.appendChild(closeBtn);
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    var previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Opt-in auto-close, e.g. data-autoclose="5" on the success block. Used by
    // the wheel enquiry, whose form already sits inside its own modal.
    var seconds = parseInt(sourceEl.getAttribute("data-autoclose") || "", 10);
    var timer = null;

    function close() {
      if (timer) clearInterval(timer);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
      // If this form lives inside the product enquiry modal, close that too so
      // the customer is returned to the page rather than a spent form.
      var host = form && form.closest ? form.closest(".inquiry-model") : null;
      if (host) host.classList.remove("model-open");
    }
    function onKey(e) {
      if (e.key === "Escape" || e.keyCode === 27) close();
    }

    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) return close(); // click the backdrop
      if (e.target.closest && e.target.closest(".native-form__modal-close, .native-form__modal-btn")) close();
    });
    document.addEventListener("keydown", onKey);

    var btn = card.querySelector(".native-form__modal-btn");
    if (btn) btn.focus();

    if (seconds > 0) {
      var left = seconds;
      var note = document.createElement("p");
      note.className = "native-form__modal-count";
      card.insertBefore(note, closeBtn);
      var tick = function () {
        note.textContent = "Closing in " + left + "…";
        if (left <= 0) return close();
        left -= 1;
      };
      tick();
      timer = setInterval(tick, 1000);
      // Any deliberate interaction cancels the countdown — nobody should have a
      // message yanked away mid-read.
      card.addEventListener("mouseenter", function () {
        if (timer) clearInterval(timer);
        if (note.parentNode) note.parentNode.removeChild(note);
      });
    }
  }

  function loading(form, on) {
    var btn = form.querySelector('button[type="submit"], .native-form__submit');
    if (!btn) return;
    if (on) {
      btn.disabled = true;
      btn.classList.add("is-loading");
      if (!btn.getAttribute("data-label")) btn.setAttribute("data-label", btn.textContent);
      btn.textContent = "Sending…";
    } else {
      btn.disabled = false;
      btn.classList.remove("is-loading");
      var l = btn.getAttribute("data-label");
      if (l) btn.textContent = l;
    }
  }

  document.addEventListener("submit", function (e) {
    var form = e.target;
    if (!form || !form.classList || !form.classList.contains("native-form__form")) return;

    var hp = form.querySelector('input[name="contact[website]"], input[name="website"]');
    if (hp && hp.value) {
      e.preventDefault(); // honeypot filled -> bot
      return;
    }

    // Let the browser's own validation run first: submit only fires here once
    // the required fields pass, so there is nothing extra to check.
    var endpoint = form.getAttribute("data-endpoint");
    if (!endpoint) {
      // native Shopify submit: just show the loading state until the reload
      loading(form, true);
      return;
    }

    // AJAX upload submit to our own endpoint (supports file attachment)
    e.preventDefault();
    loading(form, true);
    var success = form.parentNode.querySelector('[data-native-state="success"]');
    var errorBox = form.parentNode.querySelector('[data-native-state="error"]');

    function fail(code) {
      if (errorBox) {
        errorBox.textContent = ERRORS[code] || ERROR_FALLBACK;
        errorBox.style.display = "";
      }
      resetTurnstile(form);
      loading(form, false);
    }

    if (errorBox) errorBox.style.display = "none";

    // The contact form's photo picker shrinks images and validates links first
    // (assets/native-form-photos.js); every other form posts its fields as they are.
    var prepared = form.querySelector("[data-photo-picker]") && window.BajaPhotoPicker
      ? window.BajaPhotoPicker.body(form)
      : Promise.resolve(new FormData(form));

    prepared
      .then(function (body) { return fetch(endpoint, { method: "POST", body: body }); })
      .then(function (r) { return r.json().catch(function () { return { ok: r.ok }; }); })
      .then(function (data) {
        if (data && data.ok) {
          // Show the thank-you as a fixed, centred window BEFORE collapsing the
          // form. Hiding a tall form reflows the page under the reader, which
          // used to dump them into the footer with a stray message above.
          if (success) {
            showSuccessModal(success, form);
            success.style.display = "";
          }
          form.style.display = "none";
        } else {
          fail(data && data.error);
        }
      })
      .catch(function (err) {
        fail(err && err.code ? err.code : "network");
      });
  });
})();
